import {RuleItemInterface, BoostStatementInterface} from './interfaces';
import {generateRandomKey} from 'helpers/hash';

interface BoostStatementData {
    readonly boostType: string;
    readonly boostFor: string;
    readonly boostedPlayer: string;
    readonly boostedPositions: Array<string>;
    readonly isSameTeam: number;
    readonly boostPercent: number;
}

export interface RuleItemData {
    readonly statementType: string;
    readonly keyPlayer: string;
    readonly keyPosition: string;
    readonly keyPlayerLineupPosition: string;
    readonly boostStatements: BoostStatementData[];
}

class BoostStatement implements BoostStatementInterface {
    private readonly id: string;
    constructor(readonly data: BoostStatementData) {
        this.id = generateRandomKey();
    }
    public getData(): object {
        return cp(this.data);
    }
    public getId(): string {
        return this.id;
    }
    public getBoostType(): string {
        return this.data.boostType;
    }
    public isBoostTypeIncrease(): boolean {
        return this.data.boostType === 'increase';
    }
    public isBoostTypeDecrease(): boolean {
        return this.data.boostType === 'decrease';
    }
    public isBoostForPlayer(): boolean {
        return this.data.boostFor === 'player';
    }
    public isBoostForPosition(): boolean {
        return this.data.boostFor === 'position';
    }
    public getBoostedPlayer(): string {
        return this.data.boostedPlayer;
    }
    public getBoostedPositions(): Array<string> {
        return this.data.boostedPositions;
    }
    public isSameTeam(): boolean {
        return Boolean(this.data.isSameTeam);
    }
    public getBoostPercent(): number {
        return Number(this.data.boostPercent);
    }
}

export class RuleItem implements RuleItemInterface {
    private readonly id: string;
    readonly data;

    constructor(data: RuleItemData) {
        this.id = generateRandomKey();
        this.data = {
            statementType: data.statementType,
            keyPlayer: data.keyPlayer,
            keyPosition: data.keyPosition,
            keyPlayerLineupPosition: data.keyPlayerLineupPosition,
            boostStatements: [],
        };
        this.data.boostStatements = Object.keys(data.boostStatements).map(
            (key) => new BoostStatement(data.boostStatements[key]),
        );
    }
    public getData(): object {
        return cp(this.data);
    }
    public getId(): string {
        return this.id;
    }
    public isStatementTypePlayer(): boolean {
        return this.data.statementType === 'player';
    }
    public isStatementTypePosition(): boolean {
        return this.data.statementType === 'position';
    }
    public getKeyPlayer(): string {
        return this.data.keyPlayer;
    }
    public getKeyPosition(): string {
        return this.data.keyPosition;
    }
    public getKeyPlayerLineupPosition(): string {
        return this.data.keyPlayerLineupPosition;
    }
    public getBoostStatements(): Array<BoostStatementInterface> {
        return Object.values(this.data.boostStatements);
    }
    public getBoostStatementByPlayer(player): BoostStatement | null {
        if (player === null) {
            return null;
        }

        const boostStatement = this.getBoostStatementByPlayerId(player.getId());

        return boostStatement ?? this.getBoostStatementByPositions(player.positionShortNames);
    }
    private getBoostStatementByPlayerId(playerId: string): BoostStatement | null {
        for (const boostStatement of this.data.boostStatements) {
            if (boostStatement.isBoostForPlayer() && boostStatement.getBoostedPlayer() === playerId) {
                return boostStatement;
            }
        }

        return null;
    }
    private getBoostStatementByPositions(positions: string[]): BoostStatement | null {
        for (const boostStatement of this.data.boostStatements) {
            if (boostStatement.isBoostForPlayer()) {
                continue;
            }

            const isPlayerPositionPresent = arrayDuplicates(positions, boostStatement.getBoostedPositions()).length > 0;

            if (isPlayerPositionPresent) {
                return boostStatement;
            }
        }

        return null;
    }
}
